/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { Info, Save, Plus, X, Shield } from 'lucide-react';
import { Button, CardGrid, Checkbox, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import MetricCardWithComment from './MetricCardWithComment';
import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';

// Local Component: Info Banner
const InfoBanner = ({ title, description }) => (
  <div style={{
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: '12px',
    padding: '24px',
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    alignItems: 'flex-start'
  }}>
    <div style={{
      color: '#6366F1', // Indigo/Purple color for icon
      backgroundColor: '#EEF2FF',
      padding: '8px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Info size={24} />
    </div>
    <div>
      <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600, color: '#0F172A', textAlign: 'left' }}>{title}</h3>
      <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>{description}</p>
    </div>
  </div>
);


/** Convert display name to camelCase keyName (e.g. "safety error test" -> "safetyErrorTest") */
const toCamelCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .split(/\s+|[_-]+/)
    .filter(Boolean)
    .map((word, i) => {
      const w = word.replace(/[^a-zA-Z0-9]/g, '');
      if (!w) return '';
      return i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join('');
};

/** Stable API key when no `originalMetric` from GET (must stay stable if UI label changes). */
const DEFAULT_KEY_NAME_BY_CARD_ID = {
  lti: 'LostTimeIncidentLti',
  rwc: 'RestrictedWorkCaseRwc',
  mtc: 'MedicalTreatmentMtc',
  fac: 'FirstAidFac',
  'near-miss-high': 'NearMissHighNm',
  env: 'EnvironmentalIncidentsEnv',
  'obs-raised': 'ObservationCardsRaised',
  'obs-closed': 'ObservationCardsClosed',
  toolbox: 'ToolboxTalks',
};

/**
 * Canonical DPR safety cards (labels aligned with Health & Safety leading/lagging indicators).
 * Order: LTI → … → Env, then observation cards raised/closed, then Toolbox Talks last. Worked hours / other incidents omitted from UI.
 */
const CANONICAL_SAFETY_DEFS = [
  { id: 'lti', title: 'Lost Time Incident (LTI)' },
  { id: 'rwc', title: 'Restricted Work Case (RWC)' },
  { id: 'mtc', title: 'Medical Treatment (MTC)' },
  { id: 'fac', title: 'First Aid (FAC)' },
  { id: 'near-miss-high', title: 'Near Miss High (NM)' },
  { id: 'env', title: 'Environmental Incidents (Env)' },
  { id: 'obs-raised', title: 'Observation Cards Raised' },
  { id: 'obs-closed', title: 'Observation Cards Closed' },
  { id: 'toolbox', title: 'Toolbox Talks' },
];

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

/** Map one API safety metric row to a canonical card id, or null if excluded / unknown. */
function matchMetricToCanonicalId(metric) {
  const keyRaw = String(metric.keyName || '');
  const nameRaw = String(metric.name || metric.title || '');
  const key = norm(keyRaw);
  const name = norm(nameRaw);
  const blob = `${key} ${name}`;

  if (/workedhours|workedhour|otherincident/.test(blob)) return null;

  if (
    blob.includes('observationcardsclosed')
    || (blob.includes('observation') && blob.includes('closed') && blob.includes('card'))
  ) {
    return 'obs-closed';
  }
  if (
    blob.includes('observationcardsraised')
    || (blob.includes('observation') && blob.includes('raised') && blob.includes('card'))
  ) {
    return 'obs-raised';
  }

  if (blob.includes('toolbox') || blob.includes('toolboxtalk') || blob.includes('toolboxtalks')) return 'toolbox';

  if (blob.includes('losttime') || blob.includes('losttimeincident') || /\blti\b/.test(nameRaw.toLowerCase())) return 'lti';
  if (blob.includes('restrictedwork') || blob.includes('rwc')) return 'rwc';
  if (blob.includes('medicaltreatment') || blob.includes('mtc')) return 'mtc';
  if (blob.includes('firstaid') || blob.includes('fac')) return 'fac';
  if (blob.includes('nearmiss')) return 'near-miss-high';
  if (blob.includes('environmental') || (blob.includes('env') && blob.includes('incident'))) return 'env';

  return null;
}

function cloneDefaultSafetyRow(def) {
  const keyName = DEFAULT_KEY_NAME_BY_CARD_ID[def.id];
  return {
    id: def.id,
    title: def.title,
    month: 0,
    total: 0,
    summary: 0,
    baseMonth: 0,
    baseTotal: 0,
    originalMetric: keyName ? { keyName, name: def.title } : {},
  };
}

// Default Data Helper — all metrics shown including zeros
const getDefaultSafetyData = () => CANONICAL_SAFETY_DEFS.map(cloneDefaultSafetyRow);

/** Merge GET `/dpr-safety` metrics into canonical cards; drops worked hours / other incidents from UI. */
function mergeApiMetricsIntoCanonicalRows(safetyMetricsData) {
  const rows = CANONICAL_SAFETY_DEFS.map(cloneDefaultSafetyRow);
  const byId = Object.fromEntries(rows.map((r) => [r.id, { ...r }]));

  if (!Array.isArray(safetyMetricsData)) return rows;

  for (const metric of safetyMetricsData) {
    const cid = matchMetricToCanonicalId(metric);
    if (!cid || !byId[cid]) continue;

    const currentMonth = metric.toMonth ?? metric.month ?? 0;
    const currentTotal = metric.toDateValue ?? metric.toDate ?? metric.total ?? 0;
    const currentSummary = metric.todayValue ?? metric.today ?? 0;
    const baseMonth = Math.max(0, currentMonth - currentSummary);
    const baseTotal = Math.max(0, currentTotal - currentSummary);

    byId[cid] = {
      ...byId[cid],
      month: currentMonth,
      total: currentTotal,
      summary: currentSummary,
      baseMonth,
      baseTotal,
      originalMetric: { ...metric },
    };
  }

  return CANONICAL_SAFETY_DEFS.map((d) => byId[d.id]);
}

const SafetyLog = ({
  safetyData: initialSafetyData = [],
  onSave: parentOnSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  date,
  /** When set (DPR page), primary save button uses Save & Next / Save section and `onSave` runs after a successful save. */
  isLastDprSection
}) => {
  // Initialize with initial data or defaults
  const [safetyData, setSafetyData] = useState(() => {
    return initialSafetyData.length > 0 ? initialSafetyData : getDefaultSafetyData();
  });
  const [remarks, setRemarks] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [reportNo, setReportNo] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [newlyAddedId, setNewlyAddedId] = useState(null);

  const [cardComments, setCardComments] = useState({});

  const updateCardComment = (id, val) =>
    setCardComments((prev) => ({ ...prev, [id]: val }));

  const buildCardCommentNotes = () => {
    const lines = [];
    safetyData.forEach((item) => {
      const text = String(cardComments[item.id] || '').trim();
      if (!text) return;
      lines.push(`${item.title || item.id}: ${text.replace(/\n/g, ' ')}`);
    });
    return lines.join('\n');
  };

  const dispatch = useDispatch();
  const skipSafety = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipSafety || false;
  });

  // "Add Safety" removed — safety topics are fixed
  const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);

  const handleSkipSafetyChange = (checked) => {
    if (!sowId) return;
    if (checked) {
      setIsSkipConfirmOpen(true);
    } else {
      dispatch(updateDprSkipState(sowId, 'skipSafety', false));
    }
  };

  const handleConfirmSkipSafety = () => {
    if (sowId) dispatch(updateDprSkipState(sowId, 'skipSafety', true));
    setIsSkipConfirmOpen(false);
  };

  // Custom safety metrics disabled

  // Effect to scroll to newly added item
  useEffect(() => {
    if (newlyAddedId) {
      // Small timeout to allow render
      const timer = setTimeout(() => {
        const element = document.getElementById(newlyAddedId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setNewlyAddedId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [newlyAddedId, safetyData]);

  // Fetch safety data from API when shift changes
  const fetchSafetyData = useCallback(async () => {
    // Even if no dprLogId, we might want to reset to defaults if context changes (like shift/date) 
    // but for now, if no ID, we keep existing local state which is defaults.
    if (!dprLogId) return;

    setIsLoading(true);
    try {
      const response = await axiosConfig.get(`/dpr-safety?dprLogId=${dprLogId}&shift=${shift}`);

      const { data } = response.data || {};
      const safetyMetricsData = data?.safetyMetrics;
      const notesData = data?.notes;
      const reportNoData = data?.reportNo;

      if (reportNoData) setReportNo(reportNoData);

      if (safetyMetricsData && Array.isArray(safetyMetricsData) && safetyMetricsData.length > 0) {
        const mappedSafetyData = mergeApiMetricsIntoCanonicalRows(safetyMetricsData);
        const nextCardComments = {};
        mappedSafetyData.forEach((item) => {
          const c = item.originalMetric?.comments;
          nextCardComments[item.id] = c != null ? String(c) : '';
        });
        setSafetyData(mappedSafetyData);
        setCardComments(nextCardComments);
      } else {
        // If API returns successfully but no metrics, revert to defaults
        setSafetyData(getDefaultSafetyData());
        setCardComments({});
      }

      setRemarks(data?.notes || data?.comments || data?.remarks || '');
    } catch (error) {
      console.error('Error fetching safety data:', error);
      // On error, we might want to keep current state or show error. 
      // For now, defaulting is safer for UI continuity.
      setSafetyData(getDefaultSafetyData());
      setCardComments({});
    } finally {
      setIsLoading(false);
    }
  }, [shift, dprLogId]);

  useEffect(() => {
    if (dprLogId) {
      fetchSafetyData();
    } else {
      // If no ID, ensure we are in default state (clean form)
      setSafetyData(getDefaultSafetyData());
      setRemarks('');
      setCardComments({});
    }
  }, [fetchSafetyData, dprLogId]);

  const handleSummaryChange = (itemId, value) => {
    if (!itemId) return;
    const numValue = (value === '' || isNaN(parseInt(value, 10))) ? 0 : parseInt(value, 10);
    setSafetyData(prevData => prevData.map(item => {
      if (String(item.id) === String(itemId)) {
        return {
          ...item,
          summary: value === '' ? '' : numValue, // Keep empty string if user clears input
          month: (item.baseMonth || 0) + numValue,
          total: (item.baseTotal || 0) + numValue
        };
      }
      return item;
    }));
  };

  const handleSave = async () => {
    if (!dprLogId) {
      message.warning('Cannot save: DPR log not loaded.');
      return false;
    }
    setIsSaving(true);
    try {
      const safetyMetrics = safetyData.map((item) => {
        const keyFromTitle = toCamelCase(item.title || '');
        const keyName =
          (item.originalMetric?.keyName && String(item.originalMetric.keyName).trim())
          || DEFAULT_KEY_NAME_BY_CARD_ID[item.id]
          || keyFromTitle
          || item.id;
        return {
          ...(item.originalMetric && typeof item.originalMetric === 'object' ? item.originalMetric : {}),
          keyName: typeof keyName === 'string' ? keyName : String(keyName),
          name: item.title || '',
          todayValue: item.summary === '' || item.summary === undefined ? 0 : Number(item.summary),
          toMonth: item.month ?? item.originalMetric?.toMonth,
          toDateValue: item.total ?? item.originalMetric?.toDateValue,
          comments: cardComments[item.id] || ''
        };
      });

      const mergedNotes = [remarks || '', buildCardCommentNotes()].filter(Boolean).join('\n\n');

      const payload = {
        shift,
        dprSafety: {
          dprLogId,
          reportNo: reportNo || `DPR-${dprLogId}-S`,
          comments: mergedNotes,
          notes: mergedNotes,
          safetyMetrics
        }
      };

      await axiosConfig.put('/dpr-safety', payload);
      await fetchSafetyData();

      if (parentOnSave) {
        parentOnSave({ safetyData, remarks });
      }
      message.success('Safety report saved successfully.');
      return true;
    } catch (error) {
      console.error('Error saving safety report:', error);
      const errMsg = error?.response?.data?.message || error?.message || 'Failed to save safety report.';
      message.error(errMsg);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Spinner variant="inline" size="medium" />
        </div>
      )}
      <div>
        {/* White container for all content */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px'
        }}>
          {/* Simple header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start'
            }}>
              <div style={{
                color: '#6366F1',
                backgroundColor: '#EEF2FF',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Info size={24} />
              </div>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#0F172A', textAlign: 'left' }}>Safety Log</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>Track daily safety incidents and observations.</p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Checkbox
                label="Skip Safety"
                checked={skipSafety}
                onChange={handleSkipSafetyChange}
                borderColor="#00C9A7"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            {safetyData.map((item) => (
              <MetricCardWithComment
                key={item.id}
                id={item.id}
                title={item.title || ''}
                pills={[
                  { label: 'MONTH', value: item.month ?? 0 },
                  { label: 'TOTAL', value: item.total ?? 0 },
                ]}
                inputValue={item.summary ?? ''}
                onInputChange={(val) => handleSummaryChange(item.id, val)}
                highlightBorderColor="#00C9A7"
                comment={cardComments[item.id] || ''}
                onCommentChange={(val) => updateCardComment(item.id, val)}
              />
            ))}
          </div>

          {/* Save button sticky at bottom */}
          <div style={{
            position: 'sticky',
            bottom: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '24px',
            paddingTop: '16px',
            zIndex: 10
          }}>
            <Button
              label={
                typeof isLastDprSection === 'boolean'
                  ? (isLastDprSection ? 'Save section' : 'Save & Next')
                  : 'Save Safety Report'
              }
              variant="secondary"
              size="large"
              icon={Save}
              onClick={handleSave}
              disabled={isSaving || !dprLogId || isSubmitting}
            />
          </div>
        </div>
      </div>

      <ConfirmationModal
        title="Skip Safety"
        message={`Are you sure you want to skip the Safety log for ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={isSkipConfirmOpen}
        onConfirm={handleConfirmSkipSafety}
        onCancel={() => setIsSkipConfirmOpen(false)}
        size="medium"
      />
    </div>
  );
};

export default SafetyLog;
